// src/pages/Cabs.js
import React, { useRef, useState } from 'react';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';

import Footer from "./common/Footer";
import Header from "./common/Header";
import SliderCabs from './sliders/cabs/SliderCabs';
import Slider_2_Cabs from './sliders/cabs/Slider_2_Cabs';
import Slider1_Cabs from './sliders/cabs/Slider1_Cabs';
import LoginPopup from './popup/LoginPopup';
import SEO from '../components/common/SEO';
import useFrontendPageContent from '../components/common/useFrontendPageContent';
import DynamicSectionTitle from '../components/common/DynamicSectionTitle';

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['places'];

function SearchForm({ onSearch }) {
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  const fromAutoRef = useRef(null);
  const toAutoRef = useRef(null);

  const [fromLabel, setFromLabel] = useState('');
  const [toLabel, setToLabel] = useState('');

  const [fromPlace, setFromPlace] = useState(null);
  const [toPlace, setToPlace] = useState(null);

  const placeToLabel = (place) => {
    if (!place) return '';
    if (place.formatted_address) return place.formatted_address;
    if (place.name) return place.name;
    if (place.address_components && place.address_components.length) {
      const comp = place.address_components;
      const findType = (type) => {
        const c = comp.find(x => x.types && x.types.includes(type));
        return c ? c.long_name : null;
      };
      const parts = [findType('locality'), findType('administrative_area_level_1'), findType('country')].filter(Boolean);
      return parts.join(', ') || '';
    }
    return '';
  };

  const extractDetailedPlaceInfo = (place) => {
    if (!place) return {};

    const comp = place.address_components || [];
    const findType = (type) => {
      const c = comp.find(x => x.types && x.types.includes(type));
      return c ? c.long_name : null;
    };

    // Extract detailed location components
    const areaName = findType('sublocality') || findType('sublocality_level_1') || findType('neighborhood');
    const city = findType('locality') || findType('administrative_area_level_2');
    const state = findType('administrative_area_level_1');
    const buildingName = findType('premise') || findType('subpremise') || findType('establishment');
    const companyName = place.name || findType('establishment');

    return {
      areaName,
      city,
      state,
      buildingName,
      companyName,
      formattedAddress: place.formatted_address,
      placeId: place.place_id,
      geometry: place.geometry
    };
  };

  const handlePlaceChanged = (autoRef, setLabel, setPlace) => {
    const inst = autoRef.current;
    if (!inst) return;
    const place = inst.getPlace();
    const label = placeToLabel(place) || (place && place.name) || '';

    // Extract detailed place information
    const detailedInfo = extractDetailedPlaceInfo(place);

    if (label) {
      setLabel(label);
      setPlace({
        placeId: place.place_id,
        geometry: place.geometry,
        raw: place,
        detailedInfo: detailedInfo
      });
    } else {
      // user may have typed free text — keep typed value (no change)
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation: show red error text under fields instead of alert
    const newErr = {};
    if (!String(fromLabel || '').trim()) newErr.from = 'From is required.';
    if (!String(toLabel || '').trim()) newErr.to = 'To is required.';

    if (Object.keys(newErr).length) {
      setErrors(newErr);
      return; // do not submit
    }

    // Clear errors if valid
    setErrors({});

    const params = {
      from: fromLabel,
      to: toLabel,
      // Include detailed location information from Google Places API
      fromDetails: fromPlace?.detailedInfo || {},
      toDetails: toPlace?.detailedInfo || {}
    };

    const buildQueryString = (searchParams) => {
      const qp = new URLSearchParams();
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const serialized = typeof value === 'object' ? JSON.stringify(value) : String(value);
        qp.set(key, serialized);
      });
      return qp.toString();
    };

    if (typeof onSearch === 'function') {
      onSearch(params);
    } else {
      const q = buildQueryString(params);
      navigate(`/cabs/search?${q}`);
    }
  };

  return (
    <form className="filter-form" onSubmit={handleSubmit} noValidate>
      {/* From */}
      <div className="form-group cab-search-field">
        {isLoaded ? (
          <Autocomplete
            onLoad={(inst) => {
              fromAutoRef.current = inst;
              try {
                inst.setFields([
                  "formatted_address",
                  "name",
                  "address_components",
                  "geometry",
                  "place_id",
                ]);
              } catch (e) { }
            }}
            onPlaceChanged={() =>
              handlePlaceChanged(fromAutoRef, setFromLabel, setFromPlace)
            }
            options={{
              types: ["geocode", "establishment"],
              fields: ["formatted_address", "name", "address_components", "geometry", "place_id"]
            }}
          >
            <input
              id="fromInput"
              type="text"
              className={`checkin-out ${errors?.from ? "is-invalid" : ""}`}
              placeholder="Enter pickup location, e.g. Andheri East, Mumbai"
              value={fromLabel}
              onChange={(e) => {
                setFromLabel(e.target.value);
                if (errors?.from) setErrors(prev => ({ ...prev, from: null }));
              }}
              style={{ width: '100%' }}
            />
          </Autocomplete>
        ) : (
          <input
            id="fromInput"
            type="text"
            className={`checkin-out ${errors?.from ? "is-invalid" : ""}`}
            placeholder="Enter pickup location, e.g. Andheri East, Mumbai"
            value={fromLabel}
            onChange={(e) => {
              setFromLabel(e.target.value);
              if (errors?.from) setErrors(prev => ({ ...prev, from: null }));
            }}
            style={{ width: '100%' }}
          />
        )}

        <div className="cab-field-error">{errors?.from || ""}</div>
      </div>

      {/* To */}
      <div className="form-group form-border-left cab-search-field">
        {isLoaded ? (
          <Autocomplete
            onLoad={inst => {
              toAutoRef.current = inst;
              try { inst.setFields(['formatted_address', 'name', 'address_components', 'geometry', 'place_id']); } catch (e) { }
            }}
            onPlaceChanged={() => handlePlaceChanged(toAutoRef, setToLabel, setToPlace)}
            options={{
              types: ["geocode", "establishment"],
              fields: ["formatted_address", "name", "address_components", "geometry", "place_id"]
            }}
          >
            <input
              id="toInput"
              type="text"
              className={`checkin-out ${errors?.to ? "is-invalid" : ""}`}
              placeholder="Enter drop location, e.g. Connaught Place, Delhi"
              value={toLabel}
              onChange={(e) => {
                setToLabel(e.target.value);
                if (errors?.to) setErrors(prev => ({ ...prev, to: null }));
              }}
              style={{ width: '100%' }}
            />
          </Autocomplete>
        ) : (
          <input
            id="toInput"
            type="text"
            className={`checkin-out ${errors?.to ? "is-invalid" : ""}`}
            placeholder="Enter drop location, e.g. Connaught Place, Delhi"
            value={toLabel}
            onChange={(e) => {
              setToLabel(e.target.value);
              if (errors?.to) setErrors(prev => ({ ...prev, to: null }));
            }}
            style={{ width: '100%' }}
          />
        )}
        <div className="cab-field-error">{errors?.to || ""}</div>
      </div>

      <div className="form-group cab-search-submit" >
        <button type="submit" className="submit-btn-filter " style={{ width: '100%', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Search</button>
      </div>

      {loadError && <div style={{ color: 'red', marginTop: 8, width: '100%' }}>Google Maps could not be loaded — autocomplete may not work.</div>}

      <style>{`
        .filter-form {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 16px !important;
          align-items: flex-start !important;
          width: 100% !important;
        }
        .filter-form .form-group {
          margin-bottom: 0 !important;
        }
        .cab-search-field {
          min-width: 260px !important;
          flex: 1 1 0 !important;
        }
        .cab-search-submit {
          min-width: 160px !important;
          flex: 0 0 160px !important;
          padding-top: 0 !important;
        }
        .cab-field-error {
          color: #ff2a1f;
          font-size: 0.875em;
          font-weight: 600;
          min-height: 0;
          line-height: 1.35;
          margin-top: 6px;
          text-align: center;
        }
        .cab-field-error:empty {
          display: none;
        }
        @media (max-width: 767px) {
          .filter-form {
            gap: 15px !important;
            padding: 0 !important;
          }
          .filter-form .form-border-left {
            border-left: none !important;
            padding-left: 0 !important;
          }
          .filter-form .form-group {
            width: 100% !important;
            min-width: 100% !important;
            flex-basis: 100% !important;
          }
          .cab-search-field,
          .cab-search-submit {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .cab-search-submit {
            padding-top: 0 !important;
          }
          .filter-form .submit-btn-filter {
            width: 100% !important;
          }
          .cab-field-error {
            margin-top: 6px;
          }
        }
      `}</style>
    </form>
  );
}

function Cabs() {
  const { getSection, isSectionActive } = useFrontendPageContent('cabs');
  const outstationSection = getSection('outstation_cabs', {
    title: 'City To City-Outstation Cabs',
    subtitle: 'Enjoy Best Price Guarantee, professional services, timely pick-up & drop-off and more!',
  });
  const benefitsSection = getSection('benefits', {
    title: 'To Book Cab With Us',
    title_highlight: 'Benefits',
    Items: [
      { title: '10,000+ Routes Covered', description: 'We ensure that you can travel to almost any city to city ride seamlessly across India.', image_url: 'assets/img/cabs/icons/bus.png' },
      { title: 'Touring Over 20 States', description: 'No matter where you travel, EaseMyTrip provides reliable cab services in India', image_url: 'assets/img/cabs/icons/map.png' },
      { title: '100% Full Refund Policy', description: 'We offer free cancellation on cab bookings in select cases for your convenience.', image_url: 'assets/img/cabs/icons/refund.png' },
      { title: '24/7 Support', description: 'Our dedicated support team is available around the clock to assist you', image_url: 'assets/img/cabs/icons/support.png' },
    ],
  });
  const exclusiveDeals = getSection('exclusive_deals', {
    title: 'Deals and Savings',
    title_highlight: 'Unlock Exclusive',
  });
  const cabRoutes = getSection('cab_routes', {
    title: 'Popular Cab Routes',
  });
  const airportTransfer = getSection('airport_transfer', {
    title: 'Domestic Airport Transfer',
    subtitle: 'Book your guaranteed airport transfer now and enjoy peace of mind with our double refund promise!',
  });
  const cabBanner = getSection('cab_banner', {
    Items: [{ image_url: 'assets/img/cabs/banner.png', title: 'Cab Banner' }],
  });
  return (
    <div className="cabs-page">
      <SEO title="Book Reliable Cabs & Outstation Taxis | Mangoloops" description="Book the best outstation cabs and airport transfers with Mangoloops. Enjoy professional services, timely pickups, and affordable prices for your next journey." />
      <LoginPopup />
      <Header />
      <div className="banner-content bg-img-hero text-center mb-5 cabs-filter-section">
        <div className="container-fluid cabs-container-package">
          <div className="row justify-content-center py-xl-4">
            <div className="card border-0 tab-shadow cabs-filter-card">
              <div className="card-body">
                {/* Search Form */}
                <SearchForm />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isSectionActive('exclusive_deals') && <SliderCabs section={exclusiveDeals} />}

      {isSectionActive('outstation_cabs') && (
      <section className="outstation-cab holidays-left">
        <div className="container">
          <div className="cab_outstation">
            <div className="title title-text">
              <h5>Upto 15% Discount</h5>
              <h3>{outstationSection.title}</h3>
              <p>{outstationSection.subtitle}</p>
            </div>
            <div className="cab-btn org-btn">
              <a href="">Book Now</a>
            </div>

            <div className="_polrdestnbx d-flex justify-content-space-between gap-15">
              {(outstationSection.Items || []).map((item, index) => (
                <div className="ecpl_rbx cabs-city-to-city" key={index}>
                  <div className="mgdestn">
                    <img src={item.image_url} alt={item.title} />
                  </div>
                  <div className="_pckgdscrptn">
                    <h5 className="state-name-h5">{item.title}</h5>
                    <div className="htlnking">
                      {item.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {isSectionActive('cab_routes') && <Slider1_Cabs section={cabRoutes} />}

      {isSectionActive('airport_transfer') && (
      <section className="airport-transfer my-30 holidays-left">
        <div className="container">
          <div className="airport-transfer-wrap">
            <div className="ali-trans">
              <div className="title title-text ">
                <h5>Upto 10% Discount</h5>
                <h3>{airportTransfer.title}</h3>
                <p>{airportTransfer.subtitle}</p>
                <div className="cab-btn org-btn">
                  <a href="#">Book Now</a>
                </div>
              </div>

              <div className="airport-texi d-flex justify-content-space-between align-items-center _polrdestnbx  gap-15">
                {(airportTransfer.Items || []).map((item, index) => (
                  <div className="ecpl_rbx cabs-city-to-city" key={index}>
                    <a className="_city_bx" href={item.link_url || '#'} target="_blank" rel="noreferrer">
                      <div className="mgdestn">
                        <img src={item.image_url} alt={item.title} />
                      </div>
                      <div className="_pckgdscrptn">
                        <h5 className="state-name-h5">{item.title}</h5>
                      </div>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {isSectionActive('benefits') && (
      <section className="popular-state pb-5 holidays-left">
        <div className="container">
          <div className="title ">
            <DynamicSectionTitle section={benefitsSection} className="pb-5" />

            <div className="row">
              {(benefitsSection.Items || []).map((item, index) => (
                <div className="col-lg-3" key={`${item.title}-${index}`}>
                  <div className="benifit-cab">
                    <div className="icon">
                      <img src={item.image_url || ['assets/img/cabs/icons/bus.png', 'assets/img/cabs/icons/map.png', 'assets/img/cabs/icons/refund.png', 'assets/img/cabs/icons/support.png'][index % 4]} alt={item.title} />
                    </div>
                    <div className="text">
                      <h5>{item.title}</h5>
                      <p>{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {isSectionActive('cab_banner') && (
      <section className="banner-sec pt-0 mb-4 pb-5">
        <div className="container">
          <div className="row">
            <img src={cabBanner.Items?.[0]?.image_url || 'assets/img/cabs/banner.png'} alt={cabBanner.Items?.[0]?.title || ''} />
          </div>
        </div>
      </section>
      )}
      <Footer />
      <style>{`
        @media (max-width: 768px) {
          .cabs-page .cabs-container-package,
          .cabs-page .container {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
          .cabs-page .cabs-filter-section {
            margin-bottom: 16px !important;
            padding: 16px 0 !important;
          }
          .cabs-page .cabs-filter-section .row {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .cabs-page .cabs-filter-card {
            width: 100% !important;
            border-radius: 12px !important;
            margin: 0 auto !important;
          }
          .cabs-page .cabs-filter-card .card-body {
            padding: 20px !important;
          }
          .cabs-page .holidays-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .cabs-page ._polrdestnbx {
            gap: 15px !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .cabs-page .ecpl_rbx.cabs-city-to-city {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .cabs-page .banner-sec .row {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
        @media (max-width: 360px) {
          .cabs-page .cabs-filter-section {
            padding: 14px 0 !important;
          }
          .cabs-page .cabs-filter-card .card-body {
            padding: 18px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Cabs;
